// ─── Live preview engine ──────────────────────────────────────────────────────
// Handing an iframe a new srcDoc reloads the entire document and re-runs the
// Tailwind CDN script inside it, so the pane blanks, paints unstyled, then
// restyles. Doing that on every chunk of a generation is what made the preview
// strobe.
//
// Instead the frame loads a fixed shell exactly once — Tailwind, an empty user
// stylesheet, an empty body — and every update after that is applied to the live
// document: the stylesheet's text is replaced in place, and the body is morphed
// node by node against the freshly parsed HTML. Subtrees that already match are
// skipped outright, so sections already on screen (and the images they have
// loaded) are never torn down; only the part the model is actively writing
// changes. The site visibly builds itself, with zero reloads.

import {
  WIREFRAME_CSS, wireframeBlocks, mountWireframe, unmountWireframe, renderWireframe,
} from '@/lib/wireframe';
import { iconDataUri } from '@/lib/sitePages';

// Photographs are resolved server-side after the stream ends: the model writes
// `<img data-photo="hands wedging clay at a wheel" width height>` with no src,
// and a real matching image is fetched before the site is saved. That leaves a
// window during generation where the markup reserves space for a picture that
// has not arrived, so those slots get a quiet shimmer rather than a blank gap —
// the layout is already correct, only the pixels are pending.
//
// Scoped to img[data-photo] without a src, so a finished site is untouched.
// No aspect-ratio here on purpose. `attr()` outside of `content` is not
// supported by any current browser, so `aspect-ratio: attr(width)/attr(height)`
// is silently dropped — and the rule that followed it was doing nothing. The
// generator puts real width and height attributes on every image, and browsers
// already derive the ratio from those, so leaving the box alone is what keeps
// the space reserved.
const PLACEHOLDER_CSS =
  'img[data-photo]:not([src]){' +
  'background:linear-gradient(100deg,#f1f0ee 30%,#e6e4e1 50%,#f1f0ee 70%);' +
  'background-size:220% 100%;animation:bl-shimmer 1.4s ease-in-out infinite;' +
  'border-radius:inherit;min-height:64px}' +
  '@keyframes bl-shimmer{0%{background-position:180% 0}100%{background-position:-40% 0}}' +
  '@media(prefers-reduced-motion:reduce){img[data-photo]:not([src]){animation:none}}';

// Sections arrive one at a time over a minute or more, and appearing instantly
// at full contrast makes the page look like it is being assembled by accident:
// a heading lands, then a stray image, then a block of text, each one snapping
// in. Easing them in over 400ms costs nothing and turns the same sequence into
// something that reads as deliberate. Only ever applied to a node the morph has
// just created, so nothing re-animates when its text is corrected later.
//
// It must not outlive itself. It was `both` with an explicit `to{transform:none}`,
// and a filled animation beats every normal declaration — so once a section had
// entered, its OWN transform and opacity were overridden for good. A bag drawer
// that is a direct child of <body>, closed with `transform:translateX(100%)`,
// sat open over the page, and its close button (which toggles a class) did
// nothing. A page switch inserts every section at once, and ending a build on
// the home page is a page switch, so every finished store showed it; the
// published site, which never has this class, was fine.
//
// So: `backwards` with no `to` (the element's own values take over the moment it
// ends), `translate` rather than `transform` (it composes with the site's own
// transform instead of replacing it, so a drawer stays off-screen even during
// the 400ms), and the class is taken off when the animation finishes.
const ENTER_CSS =
  'body>.bl-enter{animation:bl-enter .4s cubic-bezier(.22,1,.36,1) backwards}' +
  '@keyframes bl-enter{from{opacity:0;translate:0 14px}}' +
  '@media(prefers-reduced-motion:reduce){body>.bl-enter{animation:none}}';

// The page's own background, as a stylesheet rule rather than an inline style
// on <body>.
//
// It was inline, and an inline style beats any stylesheet rule: a site whose
// CSS said `body{background:#0f0f0f}` rendered white in the preview anyway, so
// every dark or tinted design looked like it had been ignored. This block sits
// before #bl-user-css in the head, so at equal specificity the generated sheet
// wins and this is only ever the fallback for a page that has not said yet.
const BASE_CSS = 'body{margin:0;background:#ffffff}';

// The build wireframe's sheet goes AFTER the generated one. Its rules are all
// scoped to #bl-wire and its custom tags, which the generated sheet never
// names, so order only matters on a tie — and a tie should go to the frame.
export const PREVIEW_SHELL =
  '<!DOCTYPE html><html lang="en"><head><meta charset="utf-8">' +
  '<meta name="viewport" content="width=device-width, initial-scale=1">' +
  '<script src="https://cdn.tailwindcss.com"></script>' +
  '<link id="bl-favicon" rel="icon" href="data:,">' +
  `<style id="bl-base-css">${BASE_CSS}</style>` +
  `<style id="bl-placeholder-css">${PLACEHOLDER_CSS}${ENTER_CSS}</style>` +
  '<style id="bl-user-css"></style>' +
  `<style id="bl-wire-css">${WIREFRAME_CSS}</style></head>` +
  '<body></body></html>';

const USER_CSS_ID  = 'bl-user-css';
const FAVICON_ID   = 'bl-favicon';
const SCRIPT_MARK  = 'data-bl-generated';

/** Parses streamed markup off-document. DOMParser never executes scripts, and
 *  the HTML parser closes whatever tags the model has not finished writing. */
export function parseBody(html) {
  return new DOMParser().parseFromString(`<body>${html || ''}</body>`, 'text/html').body;
}

/** Lifts <script> elements out of the parsed tree. They are re-created and run
 *  deliberately once the page is complete — never mid-stream, where the source
 *  is still a fragment and would throw. */
export function takeScripts(root) {
  const found = Array.from(root.querySelectorAll('script'));
  found.forEach(s => s.remove());
  return found;
}

export function syncAttributes(target, source) {
  for (const { name, value } of Array.from(source.attributes)) {
    if (target.getAttribute(name) !== value) target.setAttribute(name, value);
  }
  for (const { name } of Array.from(target.attributes)) {
    if (!source.hasAttribute(name)) target.removeAttribute(name);
  }
}

/**
 * Copies `source`'s children onto `target` in place, touching as little as
 * possible — the isEqualNode check is what preserves already-rendered sections.
 *
 * `allowRemovals` is off while the stream is open. A refinement arrives from the
 * top, so the incoming tree is briefly shorter than what is on screen; deleting
 * the tail on every pass would collapse the page and grow it back over and over.
 * The final pass, once generation ends, runs with removals on and trims it.
 */
/**
 * Clone a node for insertion, tagging it to animate in when it is a section of
 * the page rather than a fragment of one.
 *
 * Only direct children of <body> are marked. Animating every nested node would
 * mean a section fading in while its own paragraphs fade in inside it, which
 * reads as a mess rather than a build.
 */
function cloneForInsert(node, isTopLevel) {
  const copy = node.cloneNode(true);
  if (isTopLevel && copy.nodeType === Node.ELEMENT_NODE && copy.classList) {
    copy.classList.add('bl-enter');
    // Off again once the entrance has run, so the node is the site's own
    // markup again — nothing for a page script, or a visual-edit save, to find.
    const done = (event) => {
      if (event.target !== copy) return;   // a descendant's own animation
      copy.removeEventListener('animationend', done);
      copy.classList.remove('bl-enter');
      if (!copy.classList.length) copy.removeAttribute('class');
    };
    copy.addEventListener('animationend', done);
  }
  return copy;
}

/*
 * `tail` is a node that stays last in `target` and is never morphed: the build
 * wireframe (src/lib/wireframe.js). New content is inserted above it, so the
 * page grows downward into the frame that is waiting for it. Without it, the
 * first section to arrive would land on the wireframe's index and replace it.
 */
export function morphChildren(target, source, allowRemovals, isTopLevel = false, tail = null) {
  const sChildren = source.childNodes;

  for (let i = 0; i < sChildren.length; i++) {
    const s = sChildren[i];
    let t = target.childNodes[i];
    if (t === tail) t = undefined;

    if (!t) { target.insertBefore(cloneForInsert(s, isTopLevel), tail); continue; }
    if (t.nodeType !== s.nodeType || t.nodeName !== s.nodeName) {
      target.replaceChild(cloneForInsert(s, isTopLevel), t);
      continue;
    }
    if (s.nodeType === Node.TEXT_NODE || s.nodeType === Node.COMMENT_NODE) {
      if (t.nodeValue !== s.nodeValue) t.nodeValue = s.nodeValue;
      continue;
    }
    if (s.nodeType !== Node.ELEMENT_NODE) continue;
    if (t.isEqualNode(s)) continue;

    syncAttributes(t, s);
    // Nested calls are never top level, so a section that is merely being
    // corrected does not replay its entrance.
    morphChildren(t, s, allowRemovals, false);
  }

  if (allowRemovals) {
    const keep = sChildren.length + (tail ? 1 : 0);
    while (target.childNodes.length > keep) {
      target.removeChild(tail ? tail.previousSibling : target.lastChild);
    }
  }
}

export function applyCss(doc, css) {
  const el = doc.getElementById(USER_CSS_ID);
  if (el && el.textContent !== css) el.textContent = css;
}

/**
 * Points the shell's <link rel="icon"> at the generated mark. Swapping the href
 * on the existing element keeps the document from reloading — the same reason
 * the CSS is written in place rather than re-served.
 */
export function applyIcon(doc, icon) {
  const el = doc.getElementById(FAVICON_ID);
  if (!el) return;
  // iconDataUri adds the SVG namespace a favicon needs and the model leaves off.
  const href = iconDataUri(icon) || 'data:,';
  if (el.getAttribute('href') !== href) el.setAttribute('href', href);
}

/** Scripts are re-created rather than moved: neither innerHTML nor cloneNode
 *  reliably executes an inserted <script>, so each one is rebuilt by hand. */
export function runScripts(doc, scripts, js) {
  doc.querySelectorAll(`script[${SCRIPT_MARK}]`).forEach(s => s.remove());

  const add = (attributes, text) => {
    const el = doc.createElement('script');
    attributes.forEach(({ name, value }) => el.setAttribute(name, value));
    el.setAttribute(SCRIPT_MARK, '');
    if (text) el.textContent = text;
    doc.body.appendChild(el);
  };

  scripts.forEach(s => add(Array.from(s.attributes), s.textContent));
  if (js && js.trim()) add([], js);

  replayReadyEvents(doc);
}

/**
 * Re-fires the document-ready events for scripts injected after load.
 *
 * The shell is loaded once with an empty body, so DOMContentLoaded and load
 * both fired long before any generated markup or script existed. Generated
 * script.js is injected much later, and the generator prompt explicitly tells
 * the model to "wire everything up inside a DOMContentLoaded listener" — a
 * listener registered for an event that already fired never runs, so nothing
 * binds and the site looks inert: dead mobile nav, dead tabs, dead filters.
 *
 * Exported and published sites are unaffected — there script.js is a real
 * <script src> parsed with the document, so it registers before the event.
 * This only makes the preview behave the way the shipped site already does.
 *
 * Scripts that guard on `document.readyState` already work and are not
 * disturbed: they ran their init inline and simply never registered a listener
 * for us to trigger.
 */
export function replayReadyEvents(doc) {
  const view = doc?.defaultView;
  if (!view) return;

  // Appending a <script> executes it synchronously, so by now every listener
  // the generated code installs is registered and ready to receive these.
  doc.dispatchEvent(new view.Event('DOMContentLoaded', { bubbles: true, cancelable: false }));
  view.dispatchEvent(new view.Event('load'));
}

/**
 * Applies one snapshot of a generation to an already-loaded preview document.
 *
 * @param {Document} doc                  the frame's live document
 * @param {{html?: string, css?: string, js?: string, icon?: string, path?: string}} code
 * @param {object}  options
 * @param {boolean} options.isFinal       the stream has ended; trim and run scripts
 * @param {boolean} options.scriptsAlreadyRan  a previous run left scripts bound
 * @param {object}  [options.wireframe]   a first build's structure, while it streams
 *        (see src/lib/wireframe.js); absent removes any frame still on screen
 * @returns {boolean} whether scripts are now bound to the page
 */
export function applyToDocument(doc, code, { isFinal, scriptsAlreadyRan, wireframe } = {}) {
  if (!doc?.body) return !!scriptsAlreadyRan;

  const { html, css, js, icon, path } = code || {};
  applyCss(doc, css || '');
  applyIcon(doc, icon);

  // A refinement rewrites the site from the top. If the last run's scripts are
  // still bound to nodes the morph happens to keep, the new run's copy would
  // double up on them — so a completed run starts from a clean body.
  if (isFinal && scriptsAlreadyRan) doc.body.innerHTML = '';

  // The frame of what is still to be written, kept as the body's last child so
  // the content above can grow into it. An empty list still mounts the root:
  // the blocks that just finished collapse inside it rather than vanishing.
  const blocks = wireframe ? wireframeBlocks(wireframe, path, html) : null;
  const tail = blocks ? mountWireframe(doc) : unmountWireframe(doc);

  const parsed  = parseBody(html);
  const scripts = takeScripts(parsed);
  // Direct children of <body> are the page's sections, so this call is the
  // only one that marks insertions to animate in.
  morphChildren(doc.body, parsed, !!isFinal, true, tail);
  if (tail) renderWireframe(doc, tail, blocks, wireframe.accent);

  if (!isFinal) return !!scriptsAlreadyRan;

  runScripts(doc, scripts, js || '');
  return scripts.length > 0 || !!(js && js.trim());
}
